import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { useUrlContext } from "../../context/urlContext";
import { useConsoleLog } from "../../hooks/consoleLog";
import { getCookie } from "../../utils/cookie";

interface EventFilterContextType {
  snaptrEventStatus: boolean;
  metaEventStatus: boolean;
}

const EventFilterContext = createContext<EventFilterContextType | undefined>(undefined);

export const useEventFilter = (): EventFilterContextType => {
  const context = useContext(EventFilterContext);
  if (!context) {
    throw new Error("useEventFilter must be used within EventFilterProvider");
  }
  return context;
};

interface EventFilterProviderProps {
  children: ReactNode;
}

type EventFilterKey = keyof EventFilterContextType;

interface EventFilterStatus {
  key: EventFilterKey;
  label: string;
  isAvailable: boolean;
  reason: string;
}

const SNAPTR_QUERY_PARAMS = ["ScCid", "sccid", "scid"] as const;

export const EventFilterProvider = ({ children }: EventFilterProviderProps) => {
  const { queryParams } = useUrlContext();

  const logger = useConsoleLog();

  const metaCookie = getCookie("_fbc");
  const snaptrClickParam = SNAPTR_QUERY_PARAMS.find((param) => queryParams[param]);

  const [snaptrEventStatus, setSnaptrEventStatus] = useState(
    Boolean(snaptrClickParam)
  );
  const [metaEventStatus, setMetaEventStatus] = useState(Boolean(metaCookie));

  useEffect(() => {
    if (snaptrClickParam && !snaptrEventStatus) {
      setSnaptrEventStatus(true);
    }

    if (metaCookie && !metaEventStatus) {
      setMetaEventStatus(true);
    }
  }, [metaCookie, metaEventStatus, snaptrClickParam, snaptrEventStatus]);

  const eventFilters = useMemo<EventFilterStatus[]>(
    () => [
      {
        key: "snaptrEventStatus",
        label: "Snaptr",
        isAvailable: snaptrEventStatus,
        reason: snaptrEventStatus
          ? snaptrClickParam
            ? `found ${snaptrClickParam} query param`
            : "enabled from stored Snaptr query param"
          : `missing query params: ${SNAPTR_QUERY_PARAMS.join(", ")}`,
      },
      {
        key: "metaEventStatus",
        label: "Meta",
        isAvailable: metaEventStatus,
        reason: metaEventStatus ? "found _fbc cookie" : "missing _fbc cookie",
      },
    ],
    [metaEventStatus, snaptrClickParam, snaptrEventStatus]
  );

  const contextValue = useMemo<EventFilterContextType>(
    () =>
      eventFilters.reduce<EventFilterContextType>(
        (statuses, eventFilter) => ({
          ...statuses,
          [eventFilter.key]: eventFilter.isAvailable,
        }),
        {
          snaptrEventStatus: false,
          metaEventStatus: false,
        }
      ),
    [eventFilters]
  );

  useEffect(() => {
    const availableEvents = eventFilters
      .filter(({ isAvailable }) => isAvailable)
      .map(({ label, reason }) => ({ label, reason }));

    const unavailableEvents = eventFilters
      .filter(({ isAvailable }) => !isAvailable)
      .map(({ label, reason }) => ({ label, reason }));

    logger[unavailableEvents.length ? "warn" : "info"](
      "Events availability status:",
      {
        availableEvents,
        unavailableEvents,
        events: eventFilters.map(({ label, isAvailable, reason }) => ({
          label,
          isAvailable,
          reason,
        })),
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventFilters]);

  return (
    <EventFilterContext.Provider value={contextValue}>
      {children}
    </EventFilterContext.Provider>
  );
};
